
import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { UserAchievements } from "@/data/achievements";
import { BookOpen, ChevronRight, Award, RotateCcw, GraduationCap, Sparkles } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface CategorySelectionProps {
  categories: Array<{
    id: number;
    name: string;
    created_at: string;
  }>;
  onCategorySelect: (category: string, categoryId: number) => void;
  userAchievements: UserAchievements;
  onResetProgress: () => void;
}

const CategorySelection: React.FC<CategorySelectionProps> = ({
  categories,
  onCategorySelect,
  userAchievements,
  onResetProgress,
}) => {
  console.log('CategorySelection received categories:', categories);
  const { isMobile, screenSize } = useIsMobile();
  
  // Ensure we're only working with valid database categories
  const validCategories = categories.filter(cat => cat && cat.id && cat.name);
  
  return (
    <Card className="w-full max-w-4xl mx-auto p-6 bg-gradient-to-b from-white to-blue-50 backdrop-blur-sm shadow-lg rounded-xl animate-fadeIn">
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-8 border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 animate-scaleIn shadow-sm">
              <GraduationCap className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">
                Select a Category
              </h2>
              <p className="text-gray-500 text-sm">Choose a subject area to begin your assessment</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onResetProgress}
            className={`text-sm transition-all flex items-center gap-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 active:scale-[0.97] active:bg-red-100 active:text-red-600 active:shadow-inner
              ${isMobile ? 'px-2 py-1 h-auto text-xs' : ''}`}
            size={isMobile ? "sm" : "default"}
          >
            <RotateCcw className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
            {isMobile ? 'Reset' : 'Reset Progress'}
          </Button>
        </div>
        
        {validCategories.length === 0 ? (
          <div className="text-center text-gray-500 p-12 border border-dashed border-gray-300 rounded-lg">
            <BookOpen className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-lg">No categories available</p>
            <p className="text-sm text-gray-400 mt-1">Try refreshing or adding new categories</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {validCategories.map((category) => (
                <button
                  key={category.id}
                  className="relative p-6 rounded-xl text-left h-auto flex flex-col justify-between items-start group transition-all hover-lift
                    bg-gradient-to-br from-white to-blue-50 hover:from-blue-100 hover:to-blue-200 
                    text-gray-700 border border-gray-200 hover:border-blue-300 
                    overflow-hidden shadow-sm hover:shadow-md"
                  onClick={() => onCategorySelect(category.name, category.id)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity -z-10"></div>
                  <div className="flex justify-between items-start w-full">
                    <span className="font-semibold text-lg text-gray-800 group-hover:text-gray-900 transition-colors group-active:text-gray-800">{category.name}</span>
                    <div className="p-2 rounded-full bg-blue-50 group-hover:bg-blue-200 transition-colors">
                      <ChevronRight className="h-4 w-4 text-blue-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 group-hover:border-blue-200 w-full text-xs text-gray-500 group-hover:text-blue-700 transition-colors group-active:text-blue-800">
                    <span className="inline-flex items-center">
                      <Sparkles className="h-3 w-3 mr-1 text-blue-400" />
                      Click to view subjects
                    </span>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="mt-10 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg animate-fadeIn hover-lift">
                <Award className="h-5 w-5 text-amber-500 flex-shrink-0" />
                <span className="text-amber-700">Complete quizzes to earn achievements and track your learning progress!</span>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default CategorySelection;
